import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { generateJoinCode, isValidEmail, parseEmails } from '../../../../utils/joinCodes';
import { Conference } from '../../../../utils/types';
import TableWidget from '../../../widgets/TableWidget';

interface Props {
  conference: Conference;
}

interface GeneratedCode {
  email: string;
  code: string;
}

export default function CodesTab({ conference }: Props) {
  // Inactive ticket classes are stored with negative prices (see faqs/JoinCodes.md); the server rejects their codes
  const allClasses = Object.entries(conference.prices || {}).sort(([a], [b]) => a.localeCompare(b));
  const activeClasses = allClasses.filter(([, price]) => price >= 0);

  const [ticketClass, setTicketClass] = useState<string>(activeClasses[0]?.[0] || '');
  const [emailText, setEmailText] = useState<string>('');
  const [codes, setCodes] = useState<GeneratedCode[]>([]);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);

  const handleGenerate = async () => {
    const emails = parseEmails(emailText);
    const valid = emails.filter(isValidEmail);
    setInvalidEmails(emails.filter(email => !isValidEmail(email)));

    const generated = await Promise.all(
      valid.map(async email => ({ email, code: await generateJoinCode(conference.conferenceCode, ticketClass, email) }))
    );
    setCodes(generated);
  };

  const handleDownload = () => {
    const csvString = `"Email","Join Code"
${codes.map(({ email, code }) => `"${email}","${code}"`).join('\n')}`;

    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${conference.conferenceCode}-${ticketClass}-codes.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (activeClasses.length === 0) {
    return (
      <Box sx={{ padding: '20px' }}>
        <Typography>This conference has no active ticket classes. Add one in the Details tab first.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '900px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
      <Typography variant="body2">
        Generate each delegate's personal join code. A code only works for the email it was made for,
        so use the exact email they will sign up with. Codes are not saved, but the same email always gives the same code.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', width: '250px' }}>
        <Typography variant="body2">Ticket class</Typography>
        <Select
          value={ticketClass}
          onChange={(e) => { setTicketClass(e.target.value as string); setCodes([]); }}
          size="small"
        >
          {allClasses.map(([label, price]) => (
            price >= 0
              ? <MenuItem key={label} value={label}>{label} (${price})</MenuItem>
              : <MenuItem key={label} value={label} disabled>{label} (inactive)</MenuItem>
          ))}
        </Select>
      </Box>

      {ticketClass === 'F' && (
        <Alert severity="warning">
          Free (F) codes only work if the email is also listed in functions/free_ticket_emails.csv and the functions are redeployed.
        </Alert>
      )}

      <TextField
        label="Emails (one per line, or separated by commas)"
        multiline
        minRows={6}
        value={emailText}
        onChange={(e) => setEmailText(e.target.value)}
      />

      <Box sx={{ display: 'flex', gap: '10px' }}>
        <Button variant="contained" onClick={handleGenerate} disabled={!emailText.trim()}>
          Generate codes
        </Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={codes.length === 0}>
          Download CSV
        </Button>
      </Box>

      {invalidEmails.length > 0 && (
        <Alert severity="error">
          Skipped {invalidEmails.length} invalid email{invalidEmails.length > 1 ? 's' : ''}: {invalidEmails.join(', ')}
        </Alert>
      )}

      {codes.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{codes.length} codes</Typography>
          <TableWidget rows={codes.map(({ email, code }) => [email, code])} headers={['Email', 'Join Code']} search={true} />
        </>
      )}
    </Box>
  );
}
